import { useTranslation } from 'next-i18next';

import { logger } from '@zep/core-logger';

import { HttpStatusCodeEnum } from '@/api/httpClient';
import { ALLOWED_AI_FILE_EXTENSIONS } from '@/shared/features/ai-quiz-generation/consts';
import { alertDialog } from '@/ui_v3';

type ServiceType = 'ai' | 'learningMaterial';

export const MAX_SIZE_MB: Record<ServiceType, number> = {
  ai: 30,
  learningMaterial: 20,
};

export const ALLOWED_EXTENSIONS: Record<ServiceType, string[]> = {
  // AI 첨부는 useAiFileChange 가 같은 목록으로 한 번 더 거른다. 두 게이트가 어긋나면
  // 한쪽만 통과한 파일이 조용히 막히므로 정본(ALLOWED_AI_FILE_EXTENSIONS)을 공유한다.
  ai: ALLOWED_AI_FILE_EXTENSIONS,
  learningMaterial: ['pdf'],
};

export const useValidateFile = ({ service }: { service: ServiceType }) => {
  const { t } = useTranslation(['build', 'common']);

  return (file: File) => {
    let title = '';
    let description = '';
    let errorStatus: HttpStatusCodeEnum | null = null;

    const fileExt =
      file != null ? file.name.split('.').pop()?.toLowerCase() || '' : '';

    if (file == null) {
      title = t('common:common.validateFile.requiredFile');
    } else if (file.size > MAX_SIZE_MB[service] * 1024 ** 2) {
      title = t('common:common.validateFile.confirmFileSize');
      description = t('common:common.validateFile.possibleMaxFileSize', {
        fileSize: MAX_SIZE_MB[service] + 'MB',
      });
      errorStatus = HttpStatusCodeEnum.PAYLOAD_TOO_LARGE_413;
    } else if (!ALLOWED_EXTENSIONS[service].includes(fileExt)) {
      title = t('common:common.validateFile.confirmFileExt');
      description = t('common:common.validateFile.possibleFileExt', {
        fileExt: ALLOWED_EXTENSIONS[service].join(', '),
      });
      errorStatus = HttpStatusCodeEnum.UNSUPPORTED_MEDIA_TYPE_415;
    }

    if (errorStatus && service === 'learningMaterial') {
      logger.track('upload_learningMaterial_error', {
        type: errorStatus,
        isClientError: true,
      });
    }

    if (title) {
      alertDialog.open({
        header: {
          title,
        },
        body: description,
      });
      return false;
    }

    return true;
  };
};
